import React from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { $patchStyleText } from "@lexical/selection";
import { mergeRegister } from "@lexical/utils";
import {
  $createNodeSelection,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  $nodesOfType,
  $setSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  CLICK_COMMAND,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  DROP_COMMAND,
  FORMAT_TEXT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_TAB_COMMAND,
  PASTE_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  type LexicalEditor,
  type LexicalNode,
} from "lexical";
import { useT } from "../i18n";
import { Button, Input, Tooltip } from "../ds";
import { Icon } from "../lib/icons";
import type { RichTextEditorValue } from "./workspace-review/rich-text-editor";
import {
  $createSignatureElement,
  $createSignatureMedia,
  $nearestSignatureElement,
  $signatureHtml,
  $signatureNodesFromHtml,
  SignatureElementNode,
  SignatureMediaNode,
  type SignatureEditorLogo,
} from "./signature-editor-nodes";
import {
  escapeSignatureHtml,
  normalizeSignatureEditorHtml,
  signatureEditorPlainText,
  signatureEditorStyle,
} from "./signature-editor-policy";
import {
  SIGNATURE_LOGO_ACCEPT,
  signatureImageFromTransfer,
  signatureLogoRejection,
} from "./signature-editor-clipboard";
import {
  SIGNATURE_LOGO_CORNERS,
  signatureLogoResize,
  signatureLogoSizeAt,
  signatureLogoStartSize,
  signatureLogoWidthBounds,
  type SignatureLogoCorner,
} from "./signature-editor-resize";
import styles from "./signature-editor.module.css";

export type { SignatureEditorLogo } from "./signature-editor-nodes";

const toolPaths = {
  undo: <><path d="M3 10h11a6 6 0 0 1 0 12" /><path d="m7 6-4 4 4 4" /></>,
  redo: <><path d="M21 10H10a6 6 0 0 0 0 12" /><path d="m17 6 4 4-4 4" /></>,
  bold: <><path d="M6 4h7a4 4 0 0 1 0 8H6z" /><path d="M6 12h8a4 4 0 0 1 0 8H6z" /></>,
  italic: <><path d="M10 4h10M4 20h10M15 4 9 20" /></>,
  numbered: <><path d="M10 6h11M10 12h11M10 18h11M3 4h1v4M3 14c0-2 4-2 4 0 0 1-4 3-4 4h4" /></>,
  link: <><path d="m10 13 4-4" /><path d="M8 16 6 18a4 4 0 0 1-6-6l4-4a4 4 0 0 1 6 0M14 8l2-2a4 4 0 0 1 6 6l-4 4a4 4 0 0 1-6 0" /></>,
  table: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></>,
  rule: <path d="M4 12h16" />,
  image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8" cy="8" r="1.5" /><path d="m21 15-5-5L5 21" /></>,
  code: <><path d="m8 6-6 6 6 6M16 6l6 6-6 6M14 4l-4 16" /></>,
};

function ToolIcon({ name }: { name: keyof typeof toolPaths }) {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{toolPaths[name]}</svg>;
}

function $insertBlock(block: LexicalNode): void {
  const selection = $getSelection();
  const top = $isRangeSelection(selection) ? selection.anchor.getNode().getTopLevelElement() : null;
  if (top) top.insertAfter(block);
  else $getRoot().append(block);
  if ($isElementNode(block)) block.selectStart();
}

function $tableCells(table: SignatureElementNode): SignatureElementNode[] {
  const cells: SignatureElementNode[] = [];
  const visit = (node: LexicalNode) => {
    if (node instanceof SignatureElementNode && ["td", "th"].includes(node.getTag())) cells.push(node);
    else if ($isElementNode(node)) node.getChildren().forEach(visit);
  };
  table.getChildren().forEach(visit);
  return cells;
}

function $appendTableRow(table: SignatureElementNode): SignatureElementNode {
  const row = $createSignatureElement("tr");
  const tableCells = $tableCells(table);
  const previousRow = tableCells[tableCells.length - 1]?.getParent();
  const cells = previousRow instanceof SignatureElementNode
    ? previousRow.getChildren().filter((node) => node instanceof SignatureElementNode && ["td", "th"].includes(node.getTag()))
    : [];
  for (const source of cells.length ? cells : [null, null]) {
    const attrs = source instanceof SignatureElementNode ? source.getAttributes() : {};
    delete attrs.rowspan;
    row.append($createSignatureElement("td", attrs));
  }
  const body = table.getChildren().find((node) => node instanceof SignatureElementNode && node.getTag() === "tbody");
  ($isElementNode(body) ? body : table).append(row);
  return row;
}

/** #1452: the image the resize handles and the width field act on — one, and only when selected. */
interface SelectedLogo {
  key: string;
  width: number;
  height: number;
}

function $selectedSignatureLogo(): SignatureMediaNode | null {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) return null;
  const nodes = selection.getNodes();
  const node = nodes.length === 1 ? nodes[0] : null;
  return node instanceof SignatureMediaNode && node.getTag() === "img" ? node : null;
}

function SignatureEditingPlugin({
  logoRef, onChange, onError, onEditor, onImage, onLogoSelection,
}: {
  logoRef: React.MutableRefObject<SignatureEditorLogo | null>;
  onChange: (value: RichTextEditorValue) => void;
  onError: (error: unknown) => void;
  onEditor: (editor: LexicalEditor) => void;
  /** #1451: an image arriving by paste or drop, handed to the one upload path the picker uses. */
  onImage: (file: File) => void;
  /** #1452: which image is selected and how large it currently is, or nothing. */
  onLogoSelection: (logo: SelectedLogo | null) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const callback = React.useRef(onChange);
  callback.current = onChange;
  const errorCallback = React.useRef(onError);
  errorCallback.current = onError;
  // Through a ref, like the callbacks above: a parent that passes a fresh function each render
  // must not re-register these commands.
  const imageCallback = React.useRef(onImage);
  imageCallback.current = onImage;
  const selectionCallback = React.useRef(onLogoSelection);
  selectionCallback.current = onLogoSelection;
  const currentLogo = logoRef;
  const baseline = React.useRef<string | null>(null);

  React.useEffect(() => {
    onEditor(editor);
    const emit = () => {
      try {
        const html = $signatureHtml(currentLogo.current);
        if (baseline.current === null) baseline.current = html;
        errorCallback.current(null);
        callback.current({ html, text: signatureEditorPlainText(html, currentLogo.current?.content_id ?? null), changed: html !== baseline.current });
      } catch (error) { errorCallback.current(error); }
    };
    editor.getEditorState().read(emit);
    return mergeRegister(
      editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves }) => {
        if (dirtyElements.size || dirtyLeaves.size) editorState.read(emit);
      }),
      // A separate listener, because selecting an image dirties nothing: the guard above would
      // never let the handles appear (#1452).
      editor.registerUpdateListener(({ editorState }) => editorState.read(() => {
        const node = $selectedSignatureLogo();
        if (!node) { selectionCallback.current(null); return; }
        const element = editor.getElementByKey(node.getKey());
        const natural = element instanceof HTMLImageElement
          ? { width: element.naturalWidth, height: element.naturalHeight }
          : { width: 0, height: 0 };
        selectionCallback.current({ key: node.getKey(), ...signatureLogoStartSize(node.getAttributes(), natural) });
      })),
      editor.registerCommand(PASTE_COMMAND, (event) => {
        if (!event || !("clipboardData" in event) || !event.clipboardData) return false;
        event.preventDefault();
        // An image on the clipboard is someone inserting a logo, and it wins over the HTML a
        // browser puts there beside it: that HTML points at a remote URL the signature policy
        // removes, so the text branch would swallow the image without saying anything (#1451).
        const pasted = signatureImageFromTransfer(event.clipboardData);
        if (pasted) {
          imageCallback.current(pasted);
          return true;
        }
        try {
          const rawHtml = event.clipboardData.getData("text/html");
          const plain = event.clipboardData.getData("text/plain");
          const source = rawHtml || `<p>${escapeSignatureHtml(plain).replace(/\n/g, "<br>")}</p>`;
          const nodes = $signatureNodesFromHtml(source, currentLogo.current);
          const selection = $getSelection();
          if ($isRangeSelection(selection)) selection.insertNodes(nodes);
        } catch (error) { errorCallback.current(error); }
        return true;
      }, COMMAND_PRIORITY_HIGH),
      editor.registerCommand(DROP_COMMAND, (event) => {
        // Still swallowed, whatever it carried: a drop can bring arbitrary HTML and files this
        // editor has no policy for. The one exception is an image, which takes the same upload
        // path as the toolbar button (#1451).
        event.preventDefault();
        const dropped = signatureImageFromTransfer(event.dataTransfer);
        if (dropped) imageCallback.current(dropped);
        return true;
      }, COMMAND_PRIORITY_HIGH),
      editor.registerCommand(KEY_ENTER_COMMAND, (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !$nearestSignatureElement(selection.anchor.getNode(), ["td", "th"])) return false;
        event?.preventDefault();
        selection.insertLineBreak();
        return true;
      }, COMMAND_PRIORITY_HIGH),
      editor.registerCommand(KEY_TAB_COMMAND, (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;
        const cell = $nearestSignatureElement(selection.anchor.getNode(), ["td", "th"]);
        const table = cell && $nearestSignatureElement(cell, ["table"]);
        if (!cell || !table) return false;
        event.preventDefault();
        const cells = $tableCells(table);
        const next = cells[cells.indexOf(cell) + (event.shiftKey ? -1 : 1)];
        if (next) next.selectStart();
        else if (!event.shiftKey) $appendTableRow(table).getFirstChild<SignatureElementNode>()?.selectStart();
        else table.selectPrevious();
        return true;
      }, COMMAND_PRIORITY_HIGH),
      ...[KEY_BACKSPACE_COMMAND, KEY_DELETE_COMMAND].map((command) =>
        editor.registerCommand(command, (event) => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
          const anchor = selection.anchor.getNode();
          const cell = $nearestSignatureElement(anchor, ["td", "th"]);
          if (!cell) return false;
          const backwards = command === KEY_BACKSPACE_COMMAND;
          const anchorEnd = $isElementNode(anchor) ? anchor.getChildrenSize() : anchor.getTextContentSize();
          let atEdge = selection.anchor.offset === (backwards ? 0 : anchorEnd);
          // A formatting wrapper may start in the middle of a cell. Protect only the cell
          // boundary, not every text/span boundary inside it.
          for (let node: LexicalNode | null = anchor; atEdge && node && node !== cell; node = node.getParent()) {
            if (backwards ? node.getPreviousSibling() : node.getNextSibling()) atEdge = false;
          }
          if (!atEdge) return false;
          event?.preventDefault();
          return true;
        }, COMMAND_PRIORITY_HIGH)),
      // Selecting an image is the last thing done about a click, not the first (#1455). Lexical
      // marks a pointerdown as the origin of the next selection change and then rebuilds a range
      // selection from the DOM when that change arrives, which discarded a node selection made on
      // mousedown — the handles appeared and vanished again. CLICK_COMMAND is dispatched at the
      // end of that same handling, so what it sets is what stays.
      editor.registerCommand(CLICK_COMMAND, (event) => {
        const target = event.target;
        const key = target instanceof HTMLElement ? target.getAttribute("data-signature-media") : null;
        if (!key || !$getNodeByKey(key)) return false;
        const selection = $createNodeSelection();
        selection.add(key);
        $setSelection(selection);
        return true;
      }, COMMAND_PRIORITY_LOW),
    );
  }, [editor, onEditor]);
  return null;
}

const cornerClass: Record<SignatureLogoCorner, string> = {
  nw: styles.logoNw, ne: styles.logoNe, sw: styles.logoSw, se: styles.logoSe,
};

/**
 * Corner handles over the selected logo (#1452).
 *
 * While a handle is held, the size is written straight onto the image's own `width`/`height`
 * attributes and the node is updated once, on release. Lexical observes child and text mutations
 * but not attributes, so the drag stays smooth and the undo history keeps one step instead of one
 * per pixel.
 */
function SignatureLogoHandles({ editor, logo, paperRef, onResize }: {
  editor: LexicalEditor | null;
  logo: SelectedLogo | null;
  paperRef: React.RefObject<HTMLDivElement | null>;
  onResize: (size: { width: number; height: number }) => void;
}) {
  const [box, setBox] = React.useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const drag = React.useRef<{ corner: SignatureLogoCorner; startX: number; image: HTMLElement } | null>(null);

  React.useEffect(() => {
    const image = editor && logo ? editor.getElementByKey(logo.key) : null;
    const paper = paperRef.current;
    if (!image || !paper) { setBox(null); return; }
    const measure = () => {
      const frame = image.getBoundingClientRect();
      const base = paper.getBoundingClientRect();
      // Both rectangles are relative to the viewport, while an absolutely positioned child sits
      // against the paper's unscrolled origin — hence the scroll offsets.
      const next = {
        left: frame.left - base.left + paper.scrollLeft, top: frame.top - base.top + paper.scrollTop,
        width: frame.width, height: frame.height,
      };
      setBox((current) => current && current.left === next.left && current.top === next.top
        && current.width === next.width && current.height === next.height ? current : next);
    };
    measure();
    // The image changes size under the pointer, arrives late from the network, and moves when the
    // page reflows around it. The handles have to follow all three.
    const observer = new ResizeObserver(measure);
    observer.observe(image);
    observer.observe(paper);
    image.addEventListener("load", measure);
    paper.addEventListener("scroll", measure);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      image.removeEventListener("load", measure);
      paper.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [editor, logo, paperRef]);

  if (!logo || !box) return null;
  const sizeAfter = (event: React.PointerEvent) => {
    const state = drag.current;
    return state ? signatureLogoResize(logo, event.clientX - state.startX, state.corner) : null;
  };
  const restore = () => {
    const state = drag.current;
    drag.current = null;
    if (!state) return;
    state.image.setAttribute("width", String(logo.width));
    state.image.setAttribute("height", String(logo.height));
  };
  return (
    <div className={styles.logoFrame} aria-hidden="true"
      style={{ left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` }}>
      {SIGNATURE_LOGO_CORNERS.map((corner) => (
        <span key={corner} data-corner={corner} className={`${styles.logoHandle} ${cornerClass[corner]}`}
          // Both handlers: preventing a pointerdown does not prevent the mousedown that follows,
          // and either one reaching the browser moves focus out of the editor, which drops the
          // selection and unmounts these handles mid-drag.
          onMouseDown={(event) => event.preventDefault()}
          onPointerDown={(event) => {
            const image = editor?.getElementByKey(logo.key);
            if (!image) return;
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = { corner, startX: event.clientX, image };
          }}
          onPointerMove={(event) => {
            const state = drag.current;
            const size = sizeAfter(event);
            if (!state || !size) return;
            state.image.setAttribute("width", String(size.width));
            state.image.setAttribute("height", String(size.height));
          }}
          onPointerUp={(event) => {
            const size = sizeAfter(event);
            drag.current = null;
            if (size) onResize(size);
          }}
          onPointerCancel={restore} />
      ))}
    </div>
  );
}

/** #1452: the same resize for someone who never touches a handle. */
function SignatureLogoWidthField({ logo, onResize, disabled }: {
  logo: SelectedLogo;
  onResize: (size: { width: number; height: number }) => void;
  disabled: boolean;
}) {
  const t = useT();
  const bounds = signatureLogoWidthBounds(logo);
  const [draft, setDraft] = React.useState(String(logo.width));
  // A width that arrived some other way — a corner drag — is what the field should show next.
  React.useEffect(() => { setDraft(String(logo.width)); }, [logo.key, logo.width]);
  return (
    <label className={styles.logoWidthField}>
      <span className={styles.srOnly}>{t("settings.signatureLogoWidth")}</span>
      <input className="ribbon-field" type="number" min={bounds.min} max={bounds.max} step={1}
        aria-label={t("settings.signatureLogoWidth")} value={draft} disabled={disabled}
        onChange={(event) => {
          const value = event.target.value;
          setDraft(value);
          const width = Number(value);
          // Only once it is a width we would keep: a half-typed number must not snap the image to
          // the minimum under the person's hands.
          if (Number.isInteger(width) && width >= bounds.min && width <= bounds.max) {
            onResize(signatureLogoSizeAt(logo, width));
          }
        }}
        onBlur={() => setDraft(String(logo.width))} />
      <span aria-hidden="true">px</span>
    </label>
  );
}

function SignatureToolbar({
  sourceMode, setSourceMode, uploadLogo, uploading, logo, onLogoResize,
}: {
  sourceMode: boolean;
  setSourceMode: (value: boolean) => void;
  uploadLogo: (file: File) => void;
  uploading: boolean;
  /** #1452: the selected logo, whose size this toolbar offers to the keyboard. */
  logo: SelectedLogo | null;
  onLogoResize: (size: { width: number; height: number }) => void;
}) {
  const t = useT();
  const [editor] = useLexicalComposerContext();
  const [tableSelected, setTableSelected] = React.useState(false);
  const [format, setFormat] = React.useState({ bold: false, italic: false, list: "" });
  const [canUndo, setCanUndo] = React.useState(false);
  const [canRedo, setCanRedo] = React.useState(false);
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [link, setLink] = React.useState("");
  const [linkError, setLinkError] = React.useState(false);
  const fileInput = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => mergeRegister(
    editor.registerUpdateListener(({ editorState }) => editorState.read(() => {
      const selection = $getSelection();
      setTableSelected($isRangeSelection(selection) && Boolean($nearestSignatureElement(selection.anchor.getNode(), ["table"])));
      if ($isRangeSelection(selection)) setFormat({
        bold: selection.hasFormat("bold"), italic: selection.hasFormat("italic"),
        list: $nearestSignatureElement(selection.anchor.getNode(), ["ul", "ol"])?.getTag() ?? "",
      });
    })),
    editor.registerCommand(CAN_UNDO_COMMAND, (value) => { setCanUndo(value); return false; }, COMMAND_PRIORITY_LOW),
    editor.registerCommand(CAN_REDO_COMMAND, (value) => { setCanRedo(value); return false; }, COMMAND_PRIORITY_LOW),
  ), [editor]);
  const button = (label: string, icon: React.ReactNode, action: () => void, disabled = false, pressed?: boolean, help = label) => (
    <Tooltip content={help} placement="bottom">
      <Button type="button" variant="ghost" iconOnly aria-label={label} aria-pressed={pressed}
        disabled={disabled || sourceMode} onMouseDown={(event) => event.preventDefault()} onClick={action}>{icon}</Button>
    </Tooltip>
  );
  const patchTextStyle = (property: string, value: string) => editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) $patchStyleText(selection, { [property]: value });
  });
  const align = (value: string) => editor.update(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    const element = $nearestSignatureElement(selection.anchor.getNode(), ["p", "div", "td", "th", "h1", "h2", "h3", "h4", "h5", "h6", "li"]);
    if (element) element.setStyle(signatureEditorStyle(`${element.getStyle()};text-align:${value}`));
  });
  const toggleList = (tag: "ul" | "ol") => editor.update(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    const existing = $nearestSignatureElement(selection.anchor.getNode(), ["ul", "ol"]);
    if (existing) {
      if (existing.getTag() !== tag) { existing.setTag(tag); return; }
      for (const item of existing.getChildren()) {
        if (!$isElementNode(item)) continue;
        const paragraph = $createSignatureElement().append(...item.getChildren());
        existing.insertBefore(paragraph);
      }
      existing.remove();
      return;
    }
    const block = $nearestSignatureElement(selection.anchor.getNode(), ["p", "div", "h1", "h2", "h3", "h4", "h5", "h6"]);
    const cell = $nearestSignatureElement(selection.anchor.getNode(), ["td", "th"]);
    const list = $createSignatureElement(tag);
    const item = $createSignatureElement("li");
    list.append(item);
    if (block) { block.insertBefore(list); item.append(...block.getChildren()); block.remove(); }
    else if (cell) { item.append(...cell.getChildren()); cell.append(list); }
    else $getRoot().append(list);
    item.selectEnd();
  });
  const insertTable = () => editor.update(() => {
    const table = $createSignatureElement("table", { style: "border-collapse:collapse" });
    const body = $createSignatureElement("tbody");
    const row = $createSignatureElement("tr");
    row.append(
      $createSignatureElement("td", { style: "padding-right:24px;vertical-align:top" }),
      $createSignatureElement("td", { style: "vertical-align:top" }),
    );
    table.append(body.append(row));
    $insertBlock(table);
    row.getFirstChild<SignatureElementNode>()?.selectStart();
  });
  const addRow = () => editor.update(() => {
    const selection = $getSelection();
    const table = $isRangeSelection(selection) && $nearestSignatureElement(selection.anchor.getNode(), ["table"]);
    if (table) $appendTableRow(table).getFirstChild<SignatureElementNode>()?.selectStart();
  });
  const addColumn = () => editor.update(() => {
    const selection = $getSelection();
    const table = $isRangeSelection(selection) && $nearestSignatureElement(selection.anchor.getNode(), ["table"]);
    if (!table) return;
    const rows = new Set($tableCells(table).map((cell) => cell.getParent()));
    rows.forEach((row) => {
      if ($isElementNode(row) && row.getChildrenSize() < 12) row.append($createSignatureElement("td", { style: "vertical-align:top" }));
    });
  });
  const removeTable = () => editor.update(() => {
    const selection = $getSelection();
    const table = $isRangeSelection(selection) && $nearestSignatureElement(selection.anchor.getNode(), ["table"]);
    if (table) { const paragraph = $createSignatureElement(); table.insertAfter(paragraph); table.remove(); paragraph.selectStart(); }
  });
  const applyLink = () => {
    let href: string;
    try {
      const url = new URL(link);
      if (url.protocol !== "http:" && url.protocol !== "https:") throw new Error();
      href = url.href;
    } catch { setLinkError(true); return; }
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      if (selection.isCollapsed()) {
        const anchor = $createSignatureElement("a", { href, target: "_blank", rel: "noopener noreferrer" });
        anchor.append($createTextNode(href));
        selection.insertNodes([anchor]);
        anchor.selectNext();
        return;
      }
      for (const node of selection.extract()) {
        if (!$isTextNode(node)) continue;
        const existing = $nearestSignatureElement(node, ["a"]);
        if (existing) existing.setAttribute("href", href);
        else {
          const anchor = $createSignatureElement("a", { href, target: "_blank", rel: "noopener noreferrer" });
          node.insertBefore(anchor);
          anchor.append(node);
        }
      }
    });
    setLinkOpen(false);
    editor.focus();
  };
  return (
    <div className={styles.tools}>
      <div className={styles.toolbar} role="toolbar" aria-label={t("settings.signatureFormatting")}>
        <div className={styles.toolGroup}>
          {button(t("ws.richText.undo"), <ToolIcon name="undo" />, () => editor.dispatchCommand(UNDO_COMMAND, undefined), !canUndo)}
          {button(t("ws.richText.redo"), <ToolIcon name="redo" />, () => editor.dispatchCommand(REDO_COMMAND, undefined), !canRedo)}
        </div>
        <div className={styles.toolGroup}>
          {button(t("ws.richText.bold"), <ToolIcon name="bold" />, () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold"), false, format.bold)}
          {button(t("ws.richText.italic"), <ToolIcon name="italic" />, () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic"), false, format.italic)}
          {button(t("ws.richText.bullets"), <Icon name="list" />, () => toggleList("ul"), false, format.list === "ul")}
          {button(t("ws.richText.numbering"), <ToolIcon name="numbered" />, () => toggleList("ol"), false, format.list === "ol")}
          {button(t("ws.richText.link"), <ToolIcon name="link" />, () => { setLinkError(false); setLinkOpen(!linkOpen); }, false, linkOpen)}
        </div>
        <div className={styles.toolGroup}>
          <Tooltip content={t("settings.signatureFontSize")} placement="bottom">
            <label className={styles.sizeField}>
              <span className={styles.srOnly}>{t("settings.signatureFontSize")}</span>
              <select className="ribbon-field" aria-label={t("settings.signatureFontSize")} defaultValue="12px" disabled={sourceMode}
                onChange={(event) => patchTextStyle("font-size", event.target.value)}>
                {[10, 12, 14, 16, 18, 24, 32].map((size) => <option key={size} value={`${size}px`}>{size}</option>)}
              </select>
            </label>
          </Tooltip>
          <Tooltip content={t("settings.signatureColor")} placement="bottom">
            <label className={styles.colorField}>
              <span className={styles.srOnly}>{t("settings.signatureColor")}</span>
              <input type="color" aria-label={t("settings.signatureColor")} defaultValue="#182e4c" disabled={sourceMode}
                onChange={(event) => patchTextStyle("color", event.target.value)} />
            </label>
          </Tooltip>
          <Tooltip content={t("settings.signatureAlignment")} placement="bottom">
            <label className={styles.alignField}>
              <span className={styles.srOnly}>{t("settings.signatureAlignment")}</span>
              <select className="ribbon-field" aria-label={t("settings.signatureAlignment")} defaultValue="left" disabled={sourceMode}
                onChange={(event) => align(event.target.value)}>
                {["left", "center", "right", "justify"].map((value) => <option key={value} value={value}>{t(`settings.signatureAlign.${value}` as never)}</option>)}
              </select>
            </label>
          </Tooltip>
        </div>
        <div className={styles.toolGroup}>
          {button(t("settings.signatureTable"), <ToolIcon name="table" />, insertTable)}
          {button(t("settings.signatureRule"), <ToolIcon name="rule" />, () => editor.update(() => {
            const rule = $createSignatureMedia("hr", { style: "border:0;border-top:1px solid #cccccc" });
            $insertBlock(rule);
            const after = $createSignatureElement();
            rule.insertAfter(after);
            after.selectStart();
          }))}
          {button(t("settings.signatureLogo"), uploading ? <span className="neo-btn__spin" /> : <ToolIcon name="image" />,
            () => fileInput.current?.click(), uploading, undefined, t("settings.signatureLogoHelp"))}
        </div>
        <div className={styles.sourceToggle}>
          <Button type="button" variant="ghost" size="sm" iconLeft={<ToolIcon name="code" />}
            aria-pressed={sourceMode} disabled={uploading} onClick={() => setSourceMode(!sourceMode)}>
            {t("settings.signatureSource")}
          </Button>
        </div>
        <input ref={fileInput} type="file" className={styles.fileInput} tabIndex={-1} accept={SIGNATURE_LOGO_ACCEPT}
          aria-label={t("settings.signatureLogo")} onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) uploadLogo(file);
          }} />
      </div>
      {tableSelected && !sourceMode && <div className={styles.contextTools} aria-label={t("settings.signatureTableActions")}>
        <span className={styles.contextLabel}><ToolIcon name="table" />{t("settings.signatureTableActions")}</span>
        <Button type="button" variant="ghost" size="sm" iconLeft={<Icon name="plus" />}
          onMouseDown={(event) => event.preventDefault()} onClick={addRow}>{t("settings.signatureAddRow")}</Button>
        <Button type="button" variant="ghost" size="sm" iconLeft={<Icon name="plus" />}
          onMouseDown={(event) => event.preventDefault()} onClick={addColumn}>{t("settings.signatureAddColumn")}</Button>
        <Button type="button" variant="danger-quiet" size="sm" iconLeft={<Icon name="trash" />}
          onMouseDown={(event) => event.preventDefault()} onClick={removeTable}>{t("settings.signatureRemoveTable")}</Button>
      </div>}
      {logo && !sourceMode && <div className={styles.contextTools} aria-label={t("settings.signatureLogoActions")}>
        <span className={styles.contextLabel}><ToolIcon name="image" />{t("settings.signatureLogoActions")}</span>
        <SignatureLogoWidthField logo={logo} onResize={onLogoResize} disabled={uploading} />
      </div>}
      {linkOpen && !sourceMode && <div className={styles.linkForm}>
        <Input aria-label={t("ws.richText.linkUrl")} value={link} onChange={(event) => setLink(event.target.value)} placeholder="https://" />
        <Button type="button" variant="secondary" size="sm" onClick={applyLink}>{t("settings.signatureApply")}</Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setLinkOpen(false)}>{t("common.cancel")}</Button>
        {linkError && <span className={styles.error} role="alert">{t("ws.richText.linkError")}</span>}
      </div>}
    </div>
  );
}

export function SignatureEditor({
  initialHtml, initialText, onChange, logo, onLogoChange, uploadLogo, onBusyChange, onValidityChange,
}: {
  initialHtml: string | null | undefined;
  initialText: string;
  onChange: (value: RichTextEditorValue) => void;
  logo: SignatureEditorLogo | null;
  onLogoChange: (logo: SignatureEditorLogo) => void;
  uploadLogo: (file: File) => Promise<SignatureEditorLogo>;
  onBusyChange: (busy: boolean) => void;
  onValidityChange: (valid: boolean) => void;
}) {
  const t = useT();
  const [editor, setEditor] = React.useState<LexicalEditor | null>(null);
  const [sourceMode, setSourceMode] = React.useState(false);
  const [source, setSource] = React.useState(initialHtml || `<p>${escapeSignatureHtml(initialText).replace(/\n/g, "<br>")}</p>`);
  const [error, setError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [selectedLogo, setSelectedLogo] = React.useState<SelectedLogo | null>(null);
  const paper = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => onValidityChange(error === null), [error, onValidityChange]);
  const currentLogo = React.useRef(logo);
  currentLogo.current = logo;
  const sourceBaseline = React.useRef(source);
  const initialConfig = React.useMemo(() => ({
    namespace: "TenantSignaturePolicyV1",
    nodes: [SignatureElementNode, SignatureMediaNode],
    theme: { text: { bold: styles.bold, italic: styles.italic } },
    onError(next: Error) { setError(next.message); },
    editorState() { $getRoot().clear().append(...$signatureNodesFromHtml(sourceBaseline.current, currentLogo.current)); },
  }), []);
  const fail = React.useCallback((_error: unknown) => {
    setError(_error === null ? null : t("settings.signatureContentError"));
  }, [t]);
  // The plugin reports on every editor update; holding the previous object when nothing about the
  // selected logo moved keeps the handles from remeasuring on each keystroke (#1452).
  const selectLogo = React.useCallback((next: SelectedLogo | null) => {
    setSelectedLogo((current) => (current?.key === next?.key && current?.width === next?.width
      && current?.height === next?.height ? current : next));
  }, []);
  const resizeLogo = React.useCallback((size: { width: number; height: number }) => {
    if (!editor || !selectedLogo) return;
    editor.update(() => {
      const node = $getNodeByKey(selectedLogo.key);
      if (node instanceof SignatureMediaNode) node.setSize(size);
    });
  }, [editor, selectedLogo]);
  const toggleSource = (next: boolean) => {
    if (!editor) return;
    try {
      if (next) editor.getEditorState().read(() => setSource($signatureHtml(currentLogo.current)));
      else {
        const normalized = normalizeSignatureEditorHtml(source, currentLogo.current?.content_id ?? null);
        editor.update(() => {
          $getRoot().clear().append(...$signatureNodesFromHtml(normalized, currentLogo.current));
        });
      }
      setError(null);
      setSourceMode(next);
    } catch (nextError) { fail(nextError); }
  };
  const upload = async (file: File) => {
    if (!editor) return;
    // One answer for every route in — picker, paste and drop all ask the same question, so none
    // of them can accept an image another would refuse (#1451).
    if (signatureLogoRejection(file)) {
      setError(t("settings.signatureLogoError"));
      return;
    }
    setUploading(true);
    onBusyChange(true);
    try {
      const nextLogo = await uploadLogo(file);
      currentLogo.current = nextLogo;
      onLogoChange(nextLogo);
      editor.update(() => {
        const images = $nodesOfType(SignatureMediaNode).filter((node) => node.getTag() === "img");
        if (images.length) images.forEach((node) => node.setLogo(nextLogo));
        else {
          const scale = Math.min(1, 240 / nextLogo.width, 1200 / nextLogo.height);
          const width = Math.max(1, Math.round(nextLogo.width * scale));
          const image = $createSignatureMedia("img", { src: `cid:${nextLogo.content_id}`, alt: "Firmenlogo",
            width: String(width), height: String(Math.max(1, Math.round(nextLogo.height * scale))) }, nextLogo);
          const selection = $getSelection();
          if ($isRangeSelection(selection)) selection.insertNodes([image]);
          else $getRoot().append($createSignatureElement().append(image));
        }
      });
      setError(null);
    } catch { setError(t("settings.signatureLogoError")); }
    finally { setUploading(false); onBusyChange(false); }
  };
  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={styles.editor} data-testid="signature-editor">
        <SignatureToolbar sourceMode={sourceMode} setSourceMode={toggleSource} uploadLogo={upload} uploading={uploading}
          logo={selectedLogo} onLogoResize={resizeLogo} />
        <div hidden={sourceMode} className={styles.paper} data-ribbon-theme="light" ref={paper}>
          <RichTextPlugin contentEditable={<ContentEditable className={styles.content}
            aria-label={t("settings.signatureContent")} spellCheck />}
            placeholder={null} ErrorBoundary={LexicalErrorBoundary} />
          <SignatureLogoHandles editor={editor} logo={sourceMode ? null : selectedLogo} paperRef={paper} onResize={resizeLogo} />
        </div>
        {sourceMode && <label className={styles.sourceLabel}>
          <span>{t("settings.signatureSource")}</span>
          <textarea className={styles.source} aria-label={t("settings.signatureSource")} value={source}
            spellCheck={false} onChange={(event) => {
              const value = event.target.value;
              setSource(value);
              try {
                const html = normalizeSignatureEditorHtml(value, currentLogo.current?.content_id ?? null);
                onChange({ html, text: signatureEditorPlainText(html, currentLogo.current?.content_id ?? null), changed: value !== sourceBaseline.current });
                setError(null);
              } catch (nextError) { fail(nextError); }
            }} />
          <span className={styles.hint}>{t("settings.signatureSourceHelp")}</span>
        </label>}
        {error && <p className={styles.error} role="alert">{error}</p>}
        <HistoryPlugin />
        <SignatureEditingPlugin logoRef={currentLogo} onChange={onChange} onError={fail} onEditor={setEditor}
          onImage={upload} onLogoSelection={selectLogo} />
      </div>
    </LexicalComposer>
  );
}
